import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import TextInput from '../../components/ui/TextInput.jsx';
import { formatDate } from '../../utils/Utils';
import {
  OBJECTION_STATUS_COLORS, StatusBadge,
} from './config';

// P1 Görev Paketi B — İK itiraz karar modalı (CalibrationDecisionModal'ın modal
// iskeletiyle aynı desen). Kabulde nihai seviye seçici + gerekçe zorunlu
// (kullanıcı kararı 2026-08-04: seviyeyi İK revize eder, kalibrasyon YENİDEN
// AÇILMAZ). Karara bağlanmış satırlar salt-okunur görüntülenir.
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1400;
  padding: 24px;
  overflow-y: auto;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 640px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: auto;
  margin: auto;
`;

const DialogTitle = styled.h3`
  margin: 0 0 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Section = styled.div`
  margin-bottom: 16px;
  padding-top: 14px;
  border-top: 1px solid #E8ECF0;
`;

const SectionLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 6px;
`;

const NoteBox = styled.div`
  background: #F5F7FA;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const InfoText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 6px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

export default function ObjectionDecisionModal({
  open, objection, onClose, onDecided, decide,
}) {
  const { t, i18n } = useTranslation();
  const [decision, setDecision] = useState(null);
  const [revisedLevel, setRevisedLevel] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDecision(null);
    setRevisedLevel(null);
    setDecisionNote('');
  }, [open, objection]);

  // Plan 2 (Task 8) — seçici artık itirazın döneminin skalasından gelir
  // (SortOrder sıralı, ObjectionQueueItemResponse.ScaleLevels); sabit üç
  // token'lı liste kalktı.
  const levelOptions = useMemo(() => (
    [...(objection?.scaleLevels || [])].sort((a, b) => a.sortOrder - b.sortOrder)
  ), [objection]);
  const decisionOptions = useMemo(() => [
    { id: 'accept', name: t('performance.objections.decisionAccept') },
    { id: 'reject', name: t('performance.objections.decisionReject') },
  ], [t]);

  const readOnly = objection?.status !== 'open';
  const canSubmit = !readOnly && !!decision && !!decisionNote.trim()
    && (decision === 'reject' || !!revisedLevel);

  const handleSubmit = useCallback(async () => {
    if (!objection || !canSubmit) return;
    setBusy(true);
    const ok = await decide(objection.id, decision, decisionNote.trim(), revisedLevel, objection.calibrationRecordVersion);
    setBusy(false);
    if (ok) onDecided();
  }, [objection, canSubmit, decide, decision, decisionNote, revisedLevel, onDecided]);

  if (!open || !objection) return null;

  const cfg = OBJECTION_STATUS_COLORS[objection.status] || OBJECTION_STATUS_COLORS.open;

  return (
    <Modal>
      <ModalCard>
        <DialogTitle>{t('performance.objections.detailTitle')}</DialogTitle>

        <SubTitle>
          <strong>{objection.employeeName}</strong>
          {objection.periodName && <span>— {objection.periodName}</span>}
          <StatusBadge color={cfg.color} bg={cfg.bg}>
            {t(`performance.objections.status.${objection.status}`, { defaultValue: objection.status })}
          </StatusBadge>
        </SubTitle>

        <Section>
          <SectionLabel>{t('performance.objections.col.note')}</SectionLabel>
          <NoteBox>{objection.note}</NoteBox>
          <InfoText>
            {t('performance.objections.col.createDate')}: {formatDate(objection.createDate, i18n.language)}
          </InfoText>
        </Section>

        <Section>
          <SectionLabel>{t('performance.objections.currentLevel')}</SectionLabel>
          <NoteBox>{objection.currentFinalLevelLabel || '—'}</NoteBox>
        </Section>

        {readOnly ? (
          <Section>
            <SectionLabel>{t('performance.objections.decisionResult')}</SectionLabel>
            {objection.status === 'accepted' && (
              <NoteBox>
                {objection.previousFinalLevelLabel || '—'} → {objection.revisedFinalLevelLabel || '—'}
              </NoteBox>
            )}
            {objection.decisionNote && (
              <InfoText>
                {t('performance.objections.decisionNote')}: {objection.decisionNote}
              </InfoText>
            )}
            {objection.decidedAt && (
              <InfoText>
                {t('performance.objections.decidedAt')}: {formatDate(objection.decidedAt, i18n.language)}
              </InfoText>
            )}
          </Section>
        ) : (
          <>
            <Section>
              <GenericSelect
                label={t('performance.objections.decision')}
                value={decision}
                onChange={setDecision}
                options={decisionOptions}
                idKey="id"
                labelKey="name"
                required
              />
              {decision === 'accept' && (
                <div style={{ marginTop: 12 }}>
                  <GenericSelect
                    label={t('performance.objections.revisedLevel')}
                    value={revisedLevel}
                    onChange={setRevisedLevel}
                    options={levelOptions}
                    idKey="id"
                    labelKey="label"
                    required
                  />
                  <InfoText>{t('performance.objections.acceptHint')}</InfoText>
                </div>
              )}
            </Section>

            <Section>
              <TextInput
                label={t('performance.objections.decisionNote')}
                type="textarea"
                rows={3}
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                required
              />
            </Section>
          </>
        )}

        <Actions>
          <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
          {!readOnly && (
            <Button onClick={handleSubmit} disabled={!canSubmit || busy} loading={busy}>
              {t('performance.objections.submit')}
            </Button>
          )}
        </Actions>
      </ModalCard>
    </Modal>
  );
}
